import { forwardRef, useState, useEffect } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { ApplyWrapper } from '@lib/ui';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';
import type { ColorType } from '@lib/types';

type IndicatorRenderProps = { checked: boolean };
type IndicatorProp = ReactNode | ((props: IndicatorRenderProps) => ReactNode);

const radioVariants = cva(
  [
    // Base class (provides --tw-thumb CSS variable)
    'form-radio',
    // Layout
    'relative inline-block shrink-0 cursor-pointer appearance-none overflow-hidden border',
    // Focus
    'outline-hidden focus:outline-hidden',
    // Transitions
    'transition-all duration-200 ease-out',
    // Print
    '[print-color-adjust:exact]',
    // Before pseudo-element (dot container)
    'before:absolute before:inset-0 before:h-full before:w-full before:origin-center before:scale-0',
    'before:duration-200 before:ease-out',
    // Checked state
    'checked:before:scale-100',
    // Hover
    'hover:shadow-lg',
  ],
  {
    variants: {
      variant: {
        basic: [
          // Border
          'border-line',
          // Background origin for gradient borders
          'bg-origin-border',
          // Dot icon styling
          'before:bg-center before:bg-no-repeat before:[background-size:100%_100%] before:[background-image:var(--tw-thumb)]',
          // Checked state
          'checked:border-this checked:bg-this',
          // Hover/focus states
          'hover:border-this focus:border-this',
        ],
        outlined: [
          // Border
          'border-line',
          // Dot icon styling (mask-based for outlined variant)
          'before:bg-this before:[mask-image:var(--tw-thumb)]',
          // Checked state
          'checked:border-this',
          // Hover/focus states
          'hover:border-this focus:border-this',
        ],
      },
      size: {
        xs: 'size-3.5',
        sm: 'size-4',
        md: 'size-4.5',
        lg: 'size-6',
        xl: 'size-7',
      },
      rounded: {
        default: 'rounded-full',
        square: 'rounded-md',
      },
      disabled: {
        true: [
          // Disabled styling
          'pointer-events-none select-none opacity-60',
          'border-line bg-surface-raised',
          'before:bg-fg-muted before:[mask-image:var(--tw-thumb)]',
        ],
        false: '',
      },
    },
    defaultVariants: {
      variant: 'basic',
      size: 'md',
      rounded: 'default',
      disabled: false,
    },
  }
);

const labelVariants = cva(['inline-flex items-center gap-2 cursor-pointer'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-60',
      false: '',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

type RadioProps = {
  color?: Exclude<ColorType, 'neutral'>;
  label?: ReactNode;
  /** Custom indicator element or render function. When provided, replaces the default dot. */
  indicator?: IndicatorProp;
  classNames?: {
    input?: string;
    label?: string;
    labelText?: string;
    indicator?: string;
  };
  labelProps?: Record<string, unknown>;
} & VariantProps<typeof radioVariants> &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>;

const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      variant = 'basic',
      size = 'md',
      rounded = 'default',
      color = 'primary',
      className,
      classNames = {},
      label,
      indicator,
      disabled = false,
      labelProps,
      checked: controlledChecked,
      defaultChecked,
      onChange,
      ...rest
    },
    ref
  ) => {
    // Track checked state for custom indicator render function
    const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
    const isControlled = controlledChecked !== undefined;
    const isChecked = isControlled ? controlledChecked : internalChecked;

    // Sync internal state when controlled value changes
    useEffect(() => {
      if (isControlled) {
        setInternalChecked(controlledChecked);
      }
    }, [isControlled, controlledChecked]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalChecked(e.target.checked);
      }
      onChange?.(e);
    };

    const renderIndicator = () => {
      if (!indicator) return null;
      const content =
        typeof indicator === 'function' ? indicator({ checked: isChecked }) : indicator;
      return (
        <span
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center justify-center',
            classNames?.indicator
          )}
        >
          {content}
        </span>
      );
    };

    const inputElement = (
      <input
        className={cn(
          radioVariants({ variant, size, rounded, disabled }),
          // Hide default indicator when custom indicator is provided
          indicator && 'before:hidden',
          setThisClass(color),
          className,
          classNames?.input
        )}
        disabled={disabled}
        data-disabled={disabled || undefined}
        checked={controlledChecked}
        defaultChecked={!isControlled ? defaultChecked : undefined}
        onChange={handleChange}
        type="radio"
        ref={ref}
        {...rest}
      />
    );

    // Wrap input with indicator container if custom indicator provided
    const radioElement = indicator ? (
      <span className="relative inline-flex">
        {inputElement}
        {renderIndicator()}
      </span>
    ) : (
      inputElement
    );

    return (
      <ApplyWrapper
        when={!!label}
        wrapper={(children) => (
          <label className={cn(labelVariants({ disabled }), classNames?.label)} {...labelProps}>
            {children}
            <span className={cn('select-none', classNames?.labelText)}>{label}</span>
          </label>
        )}
      >
        {radioElement}
      </ApplyWrapper>
    );
  }
);

Radio.displayName = 'Radio';

export { Radio };
export type { RadioProps };
